// src/pages/suppliers/ViewSuppliers.tsx
import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FiPlus, FiSearch, FiX } from "react-icons/fi";

import AdminLayout from "@/layouts/AdminLayout";
import ModuleAccessCard from "@/components/shared/ModuleAccessCard";

import SupplierTable from "@/components/suppliers/SupplierTable";

import {
  useSuppliersPage,
  type SupplierRow,
} from "./useSuppliersPage";
import { useSupplier } from "@/api/features/suppliers/queries";

/* ============================================================================
   Small helpers
============================================================================ */

function Breadcrumbs() {
  const { pathname } = useLocation();
  const parts = pathname.split("/").filter(Boolean);
  const crumbs = parts.map((p, idx) => {
    const href = "/" + parts.slice(0, idx + 1).join("/");
    const label = p
      .split("-")
      .map((s) => s[0]?.toUpperCase() + s.slice(1))
      .join(" ");
    return { href, label };
  });
  return (
    <nav className="text-xs text-gray-500">
      <ol className="flex flex-wrap items-center gap-1">
        <li>
          <Link to="/" className="hover:text-gray-700">
            Home
          </Link>
        </li>
        {crumbs.map((c, i) => (
          <li key={c.href} className="flex items-center gap-1">
            <span>/</span>
            {i < crumbs.length - 1 ? (
              <Link to={c.href} className="hover:text-gray-700">
                {c.label}
              </Link>
            ) : (
              <span className="text-gray-700">{c.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

function GlobalSearch({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="relative">
      <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search suppliers…"
        className="w-64 rounded-lg border border-gray-300 bg-white pl-10 pr-3 py-2 text-sm outline-none placeholder:text-gray-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
      />
    </div>
  );
}

function ConfirmDialog({
  open,
  title = "Confirm",
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  onCancel,
  onConfirm,
}: {
  open: boolean;
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/30" onClick={onCancel} />
      <div className="absolute inset-0 grid place-items-center p-4">
        <div className="w-full max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
            <button
              className="grid h-8 w-8 place-items-center rounded-md text-gray-500 hover:bg-gray-100"
              onClick={onCancel}
            >
              <FiX />
            </button>
          </div>
          <div className="p-5 text-sm text-gray-700">{message}</div>
          <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-4 py-3">
            <button
              onClick={onCancel}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              {cancelText}
            </button>
            <button
              onClick={onConfirm}
              className="rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700"
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ApprovalDecisionDialog({
  open,
  supplierName,
  loading = false,
  onCancel,
  onApprove,
  onReject,
}: {
  open: boolean;
  supplierName: string;
  loading?: boolean;
  onCancel: () => void;
  onApprove: () => void;
  onReject: () => void;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/30" onClick={onCancel} />
      <div className="absolute inset-0 grid place-items-center p-4">
        <div className="w-full max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-900">
              Review Supplier
            </h3>
            <button
              className="grid h-8 w-8 place-items-center rounded-md text-gray-500 hover:bg-gray-100"
              onClick={onCancel}
            >
              <FiX />
            </button>
          </div>
          <div className="p-5 text-sm text-gray-700">
            Choose action for <span className="font-semibold">{supplierName}</span>.
          </div>
          <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-4 py-3">
            <button
              onClick={onCancel}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              onClick={onReject}
              className="rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              disabled={loading}
            >
              Reject
            </button>
            <button
              onClick={onApprove}
              className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
              disabled={loading}
            >
              Approve
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ModulesDialog({
  open,
  title,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="absolute inset-0 grid place-items-center p-4">
        <div className="w-full max-w-5xl rounded-2xl border border-gray-200 bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
            <button
              type="button"
              onClick={onClose}
              className="grid h-8 w-8 place-items-center rounded-md text-gray-500 hover:bg-gray-100"
              aria-label="Close module access dialog"
            >
              <FiX />
            </button>
          </div>
          <div className="max-h-[80vh] overflow-y-auto p-4 sm:p-6">{children}</div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================================
   Main page
============================================================================ */

export default function ViewSuppliers() {
  const navigate = useNavigate();

  const {
    // table/data
    pageRows,
    serialStart,
    filtered,
    // search + filters
    q,
    setQ,
    filters,
    setFilters,
    // pagination
    page,
    setPage,
    pageSize,
    totalPages,
    // status
    isLoading,
    isError,
    refetch,
    // actions
    deleteSupplier,
    approveSupplier,
    rejectSupplier,
    isApproving,
    approvingId,
    isRejecting,
    rejectingId,
  } = useSuppliersPage();

  // Delete confirm
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [confirmMsg, setConfirmMsg] = React.useState("");
  const [confirmAction, setConfirmAction] =
    React.useState<() => void>(() => () => {});

  const askDelete = (row: SupplierRow) => {
    setConfirmMsg(
      `Delete supplier "${row.companyName}"? This action cannot be undone.`
    );
    setConfirmAction(() => () => {
      deleteSupplier(row.id);
      setConfirmOpen(false);
    });
    setConfirmOpen(true);
  };

  const [decisionOpen, setDecisionOpen] = React.useState(false);
  const [decisionRow, setDecisionRow] = React.useState<SupplierRow | null>(null);
  const [modulesRow, setModulesRow] = React.useState<SupplierRow | null>(null);
  const modulesDetailQuery = useSupplier(modulesRow?.id ?? null);

  const askApprove = (row: SupplierRow) => {
    setDecisionRow(row);
    setDecisionOpen(true);
  };

  const decideApprove = () => {
    if (!decisionRow) return;
    approveSupplier(decisionRow.id);
    setDecisionOpen(false);
    setDecisionRow(null);
  };

  const decideReject = () => {
    if (!decisionRow) return;
    rejectSupplier(decisionRow.id);
    setDecisionOpen(false);
    setDecisionRow(null);
  };

  const handleCreateClick = () => {
    navigate("/suppliers/new");
  };

  const handleEdit = (row: SupplierRow) => {
    navigate(`/suppliers/edit/${row.id}`, { state: { supplier: row } });
  };

  const handleManageModules = (row: SupplierRow) => {
    setModulesRow(row);
  };

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Suppliers</h1>
          <div className="mt-1">
            <Breadcrumbs />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <GlobalSearch value={q} onChange={setQ} />
          <button
            onClick={handleCreateClick}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700"
          >
            <FiPlus />
            Add Supplier
          </button>
        </div>
      </div>

      {/* Error state */}
      {isError && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 flex items-center justify-between">
          <span>Failed to load suppliers.</span>
          <button
            onClick={() => refetch()}
            className="rounded border border-red-300 bg-white px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-100"
          >
            Retry
          </button>
        </div>
      )}

      {/* Table + loading overlay */}
      <div className="relative">
        <SupplierTable
          rows={pageRows}
          serialStart={serialStart}
          filters={filters}
          setFilters={setFilters}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          totalItems={filtered.length}
          totalPages={totalPages}
          onEdit={handleEdit}
          onManageModules={handleManageModules}
          onDelete={askDelete}
          onApprove={askApprove}
          approvingId={approvingId ?? rejectingId}
        />

        {isLoading && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-white/60">
            <div className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-xs text-gray-600 shadow-sm">
              {isApproving ? "Approving supplier…" : "Loading suppliers…"}
            </div>
          </div>
        )}
      </div>

      {/* Delete confirm dialog */}
      <ConfirmDialog
        open={confirmOpen}
        title="Delete supplier"
        message={confirmMsg}
        confirmText="Delete"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={confirmAction}
      />

      <ApprovalDecisionDialog
        open={decisionOpen}
        supplierName={decisionRow?.companyName ?? "supplier"}
        loading={isApproving || isRejecting}
        onCancel={() => {
          setDecisionOpen(false);
          setDecisionRow(null);
        }}
        onApprove={decideApprove}
        onReject={decideReject}
      />

      <ModulesDialog
        open={!!modulesRow}
        title={modulesRow ? `Module Access - ${modulesRow.companyName}` : "Module Access"}
        onClose={() => setModulesRow(null)}
      >
        {modulesDetailQuery.isLoading ? (
          <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-6 text-sm text-gray-600">
            Loading supplier...
          </div>
        ) : modulesDetailQuery.isError || !modulesDetailQuery.data ? (
          <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-6 text-sm text-red-700">
            Failed to load supplier details.
          </div>
        ) : modulesDetailQuery.data.userId ? (
          <ModuleAccessCard
            userId={modulesDetailQuery.data.userId}
            entityLabel="Supplier"
          />
        ) : (
          <div className="rounded-xl border border-amber-100 bg-amber-50 px-4 py-6 text-sm text-amber-700">
            Module access will be available after this supplier has a linked login user.
          </div>
        )}
      </ModulesDialog>
    </AdminLayout>
  );
}
